import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import {
  api,
  type BundleView,
  type DealView,
  type JobView,
  type PipelineView,
  type ShortlistView,
} from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { cn } from '../../lib/utils.ts';
import { LayoutToggle } from '../boards/layout-toggle.tsx';
import { StageChip } from '../pipelines/stage-chip.tsx';
import { StartPicker, useStartPick } from '../pipelines/pipeline-stage-picker.tsx';
import { StageStepper } from '../pipelines/stage-control.tsx';
import { RecordPlaces } from './record-places.tsx';
import { CopyRcrmSlugButton } from './copy-rcrm-slug-button.tsx';
import { RecordCustomFields } from '../fields/record-custom-fields.tsx';
import { BLOCK_LIST_CLASS, BlockCard } from '../records/block-card.tsx';
import { NotesSection } from '../records/record-sidebar.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { CandidateDrawer } from '../candidates/candidate-drawer.tsx';
import { ResumeCell } from '../candidates/resume-cell.tsx';
import { AddCandidatesDialog } from './shortlist-dialogs.tsx';
import { describeAdd } from '../../lib/shortlists.ts';
import { ShortlistBoard } from './shortlist-board.tsx';
import {
  MarketplaceCandidateLink,
  MarketplaceShortlistButton,
} from './marketplace-shortlist-button.tsx';
import { nativeSelectClass } from '../ui/input.tsx';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
/** `<input type="datetime-local">` value ↔ ISO instant, in the viewer's time zone. */
const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/**
 * Jobs as expandable rows (RELAY-952): a row shows the job's title, its Job Status (a picker
 * that moves it), recruiter and interview date; expanding it shows the dates, description,
 * the job's shortlists with each candidate's Shortlist Flow stage, and the job's notes. Used by
 * the Jobs tab (every job on the project) and inside each deal's expanded row (that deal's jobs).
 */
export function JobsTable({
  jobs,
  pipeline,
  shortlistPipeline,
  canWrite,
  onChanged,
  focusJobId,
  bundles,
}: {
  jobs: JobView[];
  pipeline: PipelineView | undefined;
  shortlistPipeline: PipelineView | undefined;
  canWrite: boolean;
  onChanged: () => Promise<void>;
  /** Expand this job and bring it into view once it is in the list. */
  focusJobId?: string | undefined;
  /**
   * The deal's bundles, once loaded (never while loading: a picker over an empty list would show
   * a bundled job as "No bundle"): each row gets a picker that moves the job between them
   * (TS-925), with `job.update` (`canWrite`).
   */
  bundles?: BundleView[] | undefined;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(
    () => new Set(focusJobId ? [focusJobId] : []),
  );
  const focused = useRef<HTMLLIElement>(null);
  const hasFocusJob = !!focusJobId && jobs.some((j) => j.id === focusJobId);
  useEffect(() => {
    if (hasFocusJob) focused.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [hasFocusJob]);
  const [error, setError] = useState<string | null>(null);
  const { nameOf } = useMembers();
  const rebundle = useMutation({
    mutationFn: (v: { id: string; bundleId: string | null }) =>
      api.jobs.patch(v.id, { bundleId: v.bundleId }),
    onSuccess: async () => {
      setError(null);
      await onChanged();
    },
    onError: (err) => setError(describeError(err)),
  });
  const move = useMutation({
    mutationFn: (v: { id: string; stageId: string }) => api.jobs.moveStatus(v.id, v.stageId),
    onSuccess: async () => {
      setError(null);
      await onChanged();
    },
    onError: (err) => setError(describeError(err)),
  });
  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  if (jobs.length === 0) return <p className="text-muted-foreground text-sm">No jobs yet.</p>;
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-3" data-testid="jobs-table">
        {jobs.map((j) => (
          <li
            key={j.id}
            data-testid="job-row"
            data-job-id={j.id}
            ref={j.id === focusJobId ? focused : undefined}
            className={cn(
              'bg-card border-border overflow-hidden rounded-2xl border shadow-xs',
              j.id === focusJobId && 'ring-brand/60 ring-2',
            )}
            data-focused={j.id === focusJobId || undefined}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 rounded-lg"
                aria-expanded={open.has(j.id)}
                aria-label={`${open.has(j.id) ? 'Collapse' : 'Expand'} ${j.title}`}
                onClick={() => toggle(j.id)}
              >
                {open.has(j.id) ? <ChevronDown /> : <ChevronRight />}
              </Button>
              <span className="min-w-0 truncate font-semibold" data-testid="job-title">
                {j.title}
              </span>
              <CopyRcrmSlugButton slug={j.recruitCrmSlug} />
              <span className="flex-1" aria-hidden />
              {/* Job Status as the compact stepper; a narrow card wraps it onto its own line. */}
              {pipeline && (
                <div className="order-last flex w-full basis-full flex-col gap-1.5">
                  <StageStepper
                    size="compact"
                    pipeline={pipeline}
                    value={j.stageId}
                    disabled={!canWrite || move.isPending}
                    onMove={(stageId) => move.mutate({ id: j.id, stageId })}
                  />
                  <RecordPlaces
                    type="job"
                    places={j.pipelines}
                    disabled={!canWrite || move.isPending}
                    onMove={(stageId) => move.mutate({ id: j.id, stageId })}
                  />
                </div>
              )}
              <span className="text-muted-foreground text-xs">
                Recruiter:{' '}
                <span className="text-foreground font-medium">{nameOf(j.recruiterId)}</span>
              </span>
              <span className="text-muted-foreground text-xs">
                Interview:{' '}
                <span className="text-foreground font-medium">{when(j.interviewDateTime)}</span>
              </span>
              {bundles && bundles.length > 0 && canWrite && (
                <select
                  aria-label={`Bundle of ${j.title}`}
                  className={cn(nativeSelectClass, 'h-8 w-44')}
                  value={j.bundleId ?? ''}
                  disabled={rebundle.isPending}
                  onChange={(e) => rebundle.mutate({ id: j.id, bundleId: e.target.value || null })}
                >
                  <option value="">No bundle</option>
                  {bundles.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                  {/* In a bundle this list does not hold: still shown, so "No bundle" is a change. */}
                  {j.bundleId && !bundles.some((b) => b.id === j.bundleId) && (
                    <option value={j.bundleId}>Another bundle</option>
                  )}
                </select>
              )}
            </div>
            {open.has(j.id) && (
              <JobDetails
                job={j}
                shortlistPipeline={shortlistPipeline}
                canWrite={canWrite}
                onChanged={onChanged}
              />
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function JobDetails({
  job,
  shortlistPipeline,
  canWrite,
  onChanged,
}: {
  job: JobView;
  shortlistPipeline: PipelineView | undefined;
  canWrite: boolean;
  onChanged: () => Promise<void>;
}) {
  const { members, nameOf } = useMembers();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Shortlists shown as a board rather than a table (RELAY-1038), by id.
  const [boards, setBoards] = useState<ReadonlySet<string>>(new Set());
  // Shortlists whose archived candidates (taken off) are shown too, by id.
  const [withArchived, setWithArchived] = useState<ReadonlySet<string>>(new Set());
  const [addingTo, setAddingTo] = useState<ShortlistView | null>(null);
  const [naming, setNaming] = useState<string | null>(null);
  // A shortlisted candidate opened in the side panel, over the job (no navigation).
  const [viewing, setViewing] = useState<string | null>(null);
  const session = useSession();
  const canOpenCandidate = session.isEnabled('candidates.enabled') && session.can('candidate.read');
  const canViewResume = session.can('document.read');
  const shortlists = useQuery({
    queryKey: ['job-shortlists', job.id],
    // Archived places come too: the toggle and its count need them, and nothing else is read.
    queryFn: () => api.jobs.shortlists(job.id, { includeArchived: true }),
  });
  // A shortlist change shows in three places: this job, the candidates' Jobs tabs, the boards.
  const refreshShortlists = () =>
    Promise.all(
      [['job-shortlists', job.id], ['candidate-jobs'], ['board']].map((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      ),
    );
  const shortlistChange = useMutation({
    mutationFn: async (
      op:
        | { kind: 'create'; name: string }
        | { kind: 'status'; id: string; status: 'draft' | 'presented' | 'closed' }
        | { kind: 'delete'; id: string }
        | { kind: 'archive'; id: string; candidateId: string }
        | { kind: 'restore'; id: string; candidateId: string }
        | { kind: 'visible'; id: string; candidateId: string; visible: boolean }
        | { kind: 'replacement'; id: string; candidateId: string; replacement: boolean },
    ) => {
      if (op.kind === 'create') return api.jobs.addShortlist(job.id, op.name);
      if (op.kind === 'status') return api.jobs.patchShortlist(op.id, { status: op.status });
      if (op.kind === 'delete') return api.jobs.deleteShortlist(op.id);
      if (op.kind === 'archive') return api.jobs.archiveMember(op.id, op.candidateId);
      if (op.kind === 'restore') return api.jobs.restoreMember(op.id, op.candidateId);
      if (op.kind === 'replacement')
        return api.jobs.setMemberReplacement(op.id, op.candidateId, op.replacement);
      return api.jobs.setMemberVisibility(op.id, op.candidateId, op.visible);
    },
    onSuccess: async (_out, op) => {
      setError(null);
      setNaming(null);
      await refreshShortlists();
      // Taking off, putting back or re-kinding a hire moves the job's `filled` (TS-925).
      if (op.kind === 'archive' || op.kind === 'restore' || op.kind === 'replacement')
        await onChanged();
    },
    onError: (err) => setError(describeError(err)),
  });
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.jobs.patch>[1]) => api.jobs.patch(job.id, body),
    onSuccess: async () => {
      setError(null);
      await onChanged();
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <div
      className="border-border @container flex flex-col gap-6 border-t px-4 py-5 text-sm sm:px-6"
      data-testid="job-details"
    >
      {/* Columns follow the panel, not the window: inside a deal next to the notes sidebar
          the panel is narrow even on a wide screen. */}
      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 @lg:grid-cols-2 @4xl:grid-cols-3">
        <Field label="Recruiter">
          {canWrite ? (
            <Select
              value={job.recruiterId ?? ''}
              onValueChange={(v) => patch.mutate({ recruiterId: v || null })}
            >
              <SelectTrigger className="w-full max-w-xs" aria-label="Recruiter">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                {members
                  .filter((m) => m.status === 'active')
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          ) : (
            nameOf(job.recruiterId)
          )}
        </Field>
        <Field label="Interview">
          {canWrite ? (
            <Input
              type="datetime-local"
              aria-label="Interview date"
              className="w-full max-w-xs"
              defaultValue={toLocalInput(job.interviewDateTime)}
              onBlur={(e) => {
                const iso = e.target.value ? new Date(e.target.value).toISOString() : null;
                if (iso !== job.interviewDateTime) patch.mutate({ interviewDateTime: iso });
              }}
            />
          ) : (
            when(job.interviewDateTime)
          )}
        </Field>
        <Field label="Openings">
          {canWrite ? (
            // A hire fills one by itself (TS-925); these are for corrections and RecruitCRM's
            // history, whose hires came with the import rather than a shortlist.
            <span className="flex items-center gap-2">
              <Input
                key={`filled-${job.filled}`}
                type="number"
                min={0}
                max={job.openings}
                aria-label="Filled"
                className="w-20"
                defaultValue={job.filled}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n >= 0 && n !== job.filled)
                    patch.mutate({ filled: n });
                }}
              />
              of
              <Input
                key={`openings-${job.openings}`}
                type="number"
                min={Math.max(1, job.filled)}
                aria-label="Openings"
                className="w-20"
                defaultValue={job.openings}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n >= 0 && n !== job.openings)
                    patch.mutate({ openings: n });
                }}
              />
              filled
            </span>
          ) : (
            <>
              {job.filled} of {job.openings} filled
            </>
          )}
        </Field>
        <Field label="Opened">{when(job.openDate)}</Field>
        <Field label="Status changed">{when(job.statusChangedAt)}</Field>
        <Field label="Closed">{when(job.closedDate)}</Field>
      </dl>
      {job.content && (
        <div>
          <p className="text-muted-foreground mb-1.5 text-xs font-semibold">Description</p>
          <p className="whitespace-pre-wrap">{job.content}</p>
        </div>
      )}
      <div data-testid="job-shortlists" className="flex flex-col gap-3">
        <p className="text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase">
          Shortlists
        </p>
        {shortlists.isPending && <p className="text-muted-foreground">Loading…</p>}
        {notice && (
          <p
            className="bg-state-success-bg text-state-success-fg border-state-success-border rounded-lg border px-3 py-2"
            role="status"
          >
            {notice}
          </p>
        )}
        {shortlists.data?.map((s) => {
          // Archived places (taken off) only show while the shortlist's "Archived" toggle is on.
          const onList = s.candidates.filter((c) => !c.archivedAt);
          const archivedCount = s.candidates.length - onList.length;
          const showArchived = withArchived.has(s.id);
          const rows = showArchived ? s.candidates : onList;
          return (
            <div
              key={s.id}
              className="border-border bg-muted/40 rounded-xl border p-4"
              data-testid={`shortlist-${s.name}`}
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{s.name}</span>
                  {canWrite ? (
                    <select
                      aria-label={`Status of ${s.name}`}
                      value={s.status}
                      disabled={shortlistChange.isPending}
                      onChange={(e) =>
                        shortlistChange.mutate({
                          kind: 'status',
                          id: s.id,
                          status: e.target.value as 'draft' | 'presented' | 'closed',
                        })
                      }
                      className={cn(nativeSelectClass, 'h-7 text-xs')}
                    >
                      <option value="draft">draft</option>
                      <option value="presented">presented</option>
                      <option value="closed">closed</option>
                    </select>
                  ) : (
                    <Badge variant="secondary">{s.status}</Badge>
                  )}
                  {s.isDefault && <Badge variant="brand">default</Badge>}
                  {s.presentedAt && (
                    <span className="text-muted-foreground text-xs">
                      presented {new Date(s.presentedAt).toLocaleDateString()}
                    </span>
                  )}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {canWrite && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setNotice(null);
                        // Archived people are offered again: adding them puts their place back.
                        setAddingTo({ ...s, candidates: onList });
                      }}
                      data-testid={`add-candidates-${s.name}`}
                    >
                      <UserPlus /> Add candidates
                    </Button>
                  )}
                  <MarketplaceShortlistButton shortlist={s} candidates={onList} />
                  {(archivedCount > 0 || showArchived) && (
                    <label
                      className="text-muted-foreground flex items-center gap-1.5 text-xs"
                      title="Show the candidates taken off this shortlist"
                    >
                      <input
                        type="checkbox"
                        className="accent-brand size-3.5 cursor-pointer"
                        checked={showArchived}
                        onChange={(e) =>
                          setWithArchived((prev) => {
                            const out = new Set(prev);
                            if (e.target.checked) out.add(s.id);
                            else out.delete(s.id);
                            return out;
                          })
                        }
                        aria-label={`Show archived candidates of ${s.name}`}
                      />
                      Archived ({archivedCount})
                    </label>
                  )}
                  {canWrite && !s.isDefault && onList.length === 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      onClick={() => shortlistChange.mutate({ kind: 'delete', id: s.id })}
                      aria-label={`Delete ${s.name}`}
                    >
                      <Trash2 />
                    </Button>
                  )}
                  {shortlistPipeline && (
                    <LayoutToggle
                      layout={boards.has(s.id) ? 'board' : 'table'}
                      onChange={(next) =>
                        setBoards((prev) => {
                          const out = new Set(prev);
                          if (next === 'board') out.add(s.id);
                          else out.delete(s.id);
                          return out;
                        })
                      }
                    />
                  )}
                </span>
              </div>
              {boards.has(s.id) && shortlistPipeline ? (
                <ShortlistBoard
                  shortlistId={s.id}
                  jobId={job.id}
                  job={job}
                  pipeline={shortlistPipeline}
                  canMove={canWrite}
                  onMoved={onChanged}
                  onOpenCandidate={canOpenCandidate ? setViewing : undefined}
                  canViewResume={canViewResume}
                  includeArchived={showArchived}
                  onRestore={
                    canWrite
                      ? (candidateId) =>
                          shortlistChange.mutate({ kind: 'restore', id: s.id, candidateId })
                      : undefined
                  }
                  onError={setError}
                />
              ) : (
                <div className="bg-card border-border overflow-x-auto rounded-lg border">
                  <table className="w-full min-w-[480px] text-left text-sm" data-testid="shortlist">
                    <thead className="text-muted-foreground bg-muted/60 text-xs whitespace-nowrap">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Candidate</th>
                        <th className="px-3 py-2 font-semibold">Stage</th>
                        <th className="px-3 py-2 font-semibold">Since</th>
                        <th className="px-3 py-2 font-semibold">Client sees</th>
                        <th className="px-3 py-2 font-semibold">Resume</th>
                        <th className="px-3 py-2 font-semibold">Marketplace</th>
                        {canWrite && <th className="px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td
                            colSpan={canWrite ? 7 : 6}
                            className="text-muted-foreground px-3 py-6 text-center"
                          >
                            No candidates yet.
                            {canWrite && ' Use “Add candidates” to put people forward.'}
                          </td>
                        </tr>
                      )}
                      {rows.map((c) => (
                        <tr
                          key={c.candidateId}
                          className={cn(
                            'border-border border-t',
                            // Taken off: the row reads as disabled; only its Restore cell stays at full strength.
                            c.archivedAt &&
                              'bg-muted/50 text-muted-foreground [&>td:not([data-keep-active])]:opacity-50',
                          )}
                          data-archived={c.archivedAt ? 'true' : undefined}
                        >
                          <td className="px-3 py-2 font-medium">
                            <span className="flex flex-wrap items-center gap-2">
                              {canOpenCandidate ? (
                                <button
                                  type="button"
                                  className="cursor-pointer text-left hover:underline"
                                  onClick={() => setViewing(c.candidateId)}
                                  aria-label={`Open ${c.candidate.name}`}
                                  title={c.candidate.name}
                                >
                                  {c.candidate.name}
                                </button>
                              ) : (
                                c.candidate.name
                              )}
                              {c.archivedAt && (
                                <Badge
                                  variant="secondary"
                                  title={`Taken off ${new Date(c.archivedAt).toLocaleString()}`}
                                >
                                  Archived
                                </Badge>
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className="flex flex-wrap items-center gap-2">
                              {shortlistPipeline && (
                                <StageChip
                                  family={shortlistPipeline.family}
                                  colour={c.stage.colour as never}
                                  name={c.stage.name}
                                  terminalKind={c.stage.terminalKind}
                                />
                              )}
                              {/* A hire is a new one (fills an opening) or a replacement (TS-925). */}
                              {c.stage.terminalKind === 'won' &&
                                (canWrite ? (
                                  <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <input
                                      type="checkbox"
                                      className="accent-brand size-3.5 cursor-pointer"
                                      checked={c.isReplacement}
                                      disabled={shortlistChange.isPending || !!c.archivedAt}
                                      onChange={(e) =>
                                        shortlistChange.mutate({
                                          kind: 'replacement',
                                          id: s.id,
                                          candidateId: c.candidateId,
                                          replacement: e.target.checked,
                                        })
                                      }
                                      aria-label={`${c.candidate.name} is a replacement`}
                                    />
                                    replacement
                                  </label>
                                ) : (
                                  c.isReplacement && <Badge variant="secondary">replacement</Badge>
                                ))}
                            </span>
                          </td>
                          <td className="text-muted-foreground px-3 py-2 whitespace-nowrap">
                            {when(c.stageEnteredAt)}
                          </td>
                          <td className="px-3 py-2">
                            {canWrite ? (
                              <input
                                type="checkbox"
                                className="accent-brand size-4 cursor-pointer"
                                checked={c.visibleToClient}
                                disabled={shortlistChange.isPending || !!c.archivedAt}
                                onChange={(e) =>
                                  shortlistChange.mutate({
                                    kind: 'visible',
                                    id: s.id,
                                    candidateId: c.candidateId,
                                    visible: e.target.checked,
                                  })
                                }
                                aria-label={`Client sees ${c.candidate.name}`}
                              />
                            ) : c.visibleToClient ? (
                              'Yes'
                            ) : (
                              'No'
                            )}
                          </td>
                          <td className="px-3 py-1">
                            {/* No upload here: a resume is added on the candidate. "—" without one. */}
                            <ResumeCell
                              candidate={c.candidate}
                              canUpload={false}
                              canDownload={canViewResume}
                              onChanged={() => undefined}
                              onError={setError}
                            />
                          </td>
                          <td className="px-3 py-1">
                            <MarketplaceCandidateLink
                              name={c.candidate.name}
                              code={c.candidate.marketplaceCode}
                            />
                          </td>
                          {canWrite && (
                            <td className="px-3 py-2 text-right" data-keep-active>
                              {c.archivedAt ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-muted-foreground size-8"
                                  disabled={shortlistChange.isPending}
                                  onClick={() =>
                                    shortlistChange.mutate({
                                      kind: 'restore',
                                      id: s.id,
                                      candidateId: c.candidateId,
                                    })
                                  }
                                  aria-label={`Restore ${c.candidate.name} to ${s.name}`}
                                  title="Put back on the shortlist"
                                >
                                  <ArchiveRestore />
                                </Button>
                              ) : (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-muted-foreground hover:text-danger size-8"
                                  disabled={shortlistChange.isPending}
                                  onClick={() =>
                                    shortlistChange.mutate({
                                      kind: 'archive',
                                      id: s.id,
                                      candidateId: c.candidateId,
                                    })
                                  }
                                  aria-label={`Archive ${c.candidate.name} on ${s.name}`}
                                  title="Take off the shortlist (archived: the Archived toggle shows them)"
                                >
                                  <Archive />
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {canWrite &&
          shortlists.data &&
          (naming === null ? (
            <Button
              size="sm"
              className="self-start"
              onClick={() => setNaming(`Shortlist ${shortlists.data.length + 1}`)}
              data-testid="new-shortlist"
            >
              <Plus /> New shortlist
            </Button>
          ) : (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (naming.trim()) shortlistChange.mutate({ kind: 'create', name: naming.trim() });
              }}
            >
              <Input
                autoFocus
                aria-label="New shortlist name"
                value={naming}
                onChange={(e) => setNaming(e.target.value)}
                className="bg-card h-9 w-full sm:w-64"
              />
              <Button
                size="sm"
                type="submit"
                disabled={!naming.trim() || shortlistChange.isPending}
              >
                Create
              </Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => setNaming(null)}>
                Cancel
              </Button>
            </form>
          ))}
      </div>
      {/* The job's custom fields (TS-1840) on the left (60%) and its own notes on the right (40%),
          side by side once the panel has room (this panel is the `@container`), stacked when
          narrow. Notes written here go on the job, and the project's Notes list shows them too,
          badged with the job. Custom fields are absent when no job field is defined; editing
          follows `canWrite` (job.update) and each field's role access. */}
      <div className="grid gap-5 @3xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <RecordCustomFields
          entity="jobs"
          values={job.custom}
          canWrite={canWrite}
          save={(custom) => api.jobs.patch(job.id, { custom })}
          onSaved={onChanged}
          testId="job-custom-fields"
          frame="section"
        />
        <div data-testid="job-notes" className="@3xl:col-start-2">
          <BlockCard title="Notes">
            {(full) => (
              <NotesSection
                entityType="jobs"
                entityId={job.id}
                canWrite={canWrite}
                openable={false}
                listClassName={full ? '' : BLOCK_LIST_CLASS}
              />
            )}
          </BlockCard>
        </div>
      </div>
      {error && (
        <p className="text-danger" role="alert">
          {error}
        </p>
      )}
      <CandidateDrawer
        candidateId={viewing}
        onClose={() => setViewing(null)}
        onChanged={async () => {
          await refreshShortlists();
        }}
      />
      {addingTo && (
        <AddCandidatesDialog
          shortlist={addingTo}
          jobTitle={job.title}
          pipeline={shortlistPipeline}
          onClose={() => setAddingTo(null)}
          onDone={async (result) => {
            setNotice(describeAdd(result, addingTo.name));
            setAddingTo(null);
            await refreshShortlists();
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // Label above value: side by side, a fixed-width control ran into the next column's label.
    <div className="flex min-w-0 flex-col gap-1.5">
      <dt className="text-muted-foreground text-xs font-semibold">{label}</dt>
      <dd className="min-w-0 font-medium">{children}</dd>
    </div>
  );
}

/**
 * "Add job" under a deal: the title is enough, with the Job Flow pipeline it starts in and its
 * stage (the default pipeline's first open stage unless picked; an extra pipeline gives the job a
 * place there, its status still starting at the default's first open stage). Everything else is
 * edited on the row.
 */
export function AddJobForm({
  deals,
  dealId,
  bundles,
  onCreated,
}: {
  deals: DealView[];
  /** Fixed when adding from inside a deal's row; otherwise the user picks the deal. */
  dealId?: string | undefined;
  /** The deal's bundles, when adding inside a deal's row: the new job may go into one. */
  bundles?: BundleView[] | undefined;
  onCreated: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [deal, setDeal] = useState(dealId ?? deals[0]?.id ?? '');
  const [picked, setPicked] = useState('');
  // A bundle deleted (or no longer listed) since it was picked is no choice at all.
  const bundle = bundles?.some((b) => b.id === picked) ? picked : '';
  const start = useStartPick('job');
  const { pipelineId, stageId } = start.value;
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.jobs.create({
        dealId: dealId ?? deal,
        title: title.trim(),
        ...(bundle ? { bundleId: bundle } : {}),
        ...(pipelineId && { pipelineId }),
        ...(stageId && { stageId }),
      }),
    onSuccess: async () => {
      setTitle('');
      setPicked('');
      setError(null);
      await qc.invalidateQueries({ queryKey: ['job-shortlists'] });
      await onCreated();
    },
    onError: (err) => setError(describeError(err)),
  });
  if (deals.length === 0)
    return (
      <p className="text-muted-foreground text-sm">Add a deal first: jobs sit under a deal.</p>
    );
  return (
    <form
      className="flex flex-wrap items-center gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) create.mutate();
      }}
    >
      <Input
        aria-label="Job title"
        placeholder="New job title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="bg-card w-full sm:w-72"
      />
      {dealId && bundles && bundles.length > 0 && (
        <select
          aria-label="Into bundle"
          className={cn(nativeSelectClass, 'bg-card w-full sm:w-56')}
          value={bundle}
          onChange={(e) => setPicked(e.target.value)}
        >
          <option value="">No bundle (standalone)</option>
          {bundles.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      )}
      {!dealId && (
        <Select value={deal} onValueChange={setDeal}>
          <SelectTrigger className="w-full sm:w-56" aria-label="Under deal">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {deals.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <StartPicker pick={start} className="bg-card w-full sm:w-52" />
      <Button type="submit" disabled={!title.trim() || !stageId || create.isPending}>
        <Plus /> Add job
      </Button>
      {error && (
        <p className="text-danger w-full text-sm" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
